import { Component, type ErrorInfo, type ReactNode } from 'react';
import { BraindayMark } from '@/components/brand';
import { recordError } from '@/lib/crashReporter';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AppErrorBoundary]', error, info.componentStack);
    recordError(error, info.componentStack ? { componentStack: info.componentStack } : undefined);
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-6 bg-canvas px-6 text-center">
          <BraindayMark size={64} />
          <h1 className="text-head font-semibold text-t1">Something went wrong</h1>
          <p className="max-w-[280px] text-sm text-t2">
            An unexpected error occurred. Reloading usually fixes it.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="h-10 rounded-lg bg-t1 px-5 text-sm font-medium text-canvas can-hover:hover:bg-t1-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
